import React, { useContext, useReducer } from 'react';
import { ReadBookmark } from '@/infrastructure/class/read-bookmark';
import { ReadSurah } from '@/infrastructure/class/read-surah';
import { firebaseApp } from '@/infrastructure/firebase/firebase-app';
import {
  FIRESTORE_COLLECTIONS,
  FIRESTORE_DOCS,
} from '@/infrastructure/enum/firestore';
import {
  ILastRead,
  IReadBookmark,
} from '@/infrastructure/interfaces/i-read-bookmark';
import { removeElementsFromArray } from '@/utils/helpers/remove-elements-from-array';
import type { User } from 'firebase/auth';
import { IReadSurah } from '@/infrastructure/interfaces/i-read-surah';

const getApiClient = async () => {
  const { Api } = await import('@/api');
  return Api.getInstance();
};

const getFirestoreClient = async () => {
  const firestore = await import('firebase/firestore');
  return {
    db: firestore.getFirestore(firebaseApp),
    ...firestore,
  };
};

const readBookmarkCollectionRef = (
  db: any,
  collectionFn: any,
  userUid: string
) =>
  collectionFn(
    db,
    FIRESTORE_COLLECTIONS.USERS,
    userUid,
    FIRESTORE_COLLECTIONS.READBOOKMARKS
  );
const readBookmarkDocRef = (
  db: any,
  docFn: any,
  userUid: string,
  docId: string
) =>
  docFn(
    db,
    FIRESTORE_COLLECTIONS.USERS,
    userUid,
    FIRESTORE_COLLECTIONS.READBOOKMARKS,
    docId
  );
const lastReadDocRef = (db: any, docFn: any, userUid: string) =>
  docFn(
    db,
    FIRESTORE_COLLECTIONS.USERS,
    userUid,
    FIRESTORE_COLLECTIONS.READER,
    FIRESTORE_DOCS.CONTINUE
  );

export interface IReadBookmarksContext {
  readBookmarks: IReadBookmarkState;
  loadReadBookmarks: (user: User) => void;
  loadLastRead: (user: User) => void;
  saveLastRead: (payload: IBaseReadBookmarkUpdateEffect) => void;
  changeReadBookmark: (payload: IBaseReadBookmarkUpdateEffect) => void;
  isBookmarked: (surah: ReadSurah, timing?: number) => ReadBookmark | undefined;
}

interface IReadBookmarkState {
  items: ReadBookmark[];
  isBookmark: (surah: ReadSurah, timing?: number) => ReadBookmark | undefined;
}

export interface IBaseReadBookmarkUpdateEffect {
  user: User;
  readSurah: ReadSurah;
  ayaNumber?: number;
}

export interface IDeleteReadBookmarkEffect {
  user: User;
  bookmark: ReadBookmark;
}

interface IReadBookmarksProvider {
  children: React.ReactNode;
}

enum BookmarkAction {
  SET_BOOKMARKS = 'SET_BOOKMARKS',
  SET_LAST_READ = 'SET_LAST_READ',
  ADD_READ_BOOKMARK = 'ADD_READ_BOOKMARK',
  REMOVE_READ_BOOKMARK = 'REMOVE_READ_BOOKMARK',
}

type SetBookmarksAction = {
  type: BookmarkAction.SET_BOOKMARKS;
  items: IReadBookmark[];
};

type SetLastReadAction = {
  type: BookmarkAction.SET_LAST_READ;
  bookmark: IReadBookmark;
};

type AddReadBookmarkAction = {
  type: BookmarkAction.ADD_READ_BOOKMARK;
  bookmark: IReadBookmark;
};

type RemoveReadBookmarkAction = {
  type: BookmarkAction.REMOVE_READ_BOOKMARK;
  bookmark: ReadBookmark;
};

type ActionTypes =
  | SetBookmarksAction
  | SetLastReadAction
  | AddReadBookmarkAction
  | RemoveReadBookmarkAction;

function reducerFunc(state: IReadBookmarkState, action: ActionTypes) {
  switch (action.type) {
    case BookmarkAction.SET_BOOKMARKS: {
      return {
        ...state,
        items: action.items.map(
          (item: IReadBookmark) => new ReadBookmark(item)
        ),
      };
    }
    case BookmarkAction.SET_LAST_READ: {
      const bookmark = action.bookmark as IReadBookmark;
      return {
        ...state,
        items: [
          new ReadBookmark(bookmark, bookmark.isLastRead),
          ...state.items.filter((el) => !el.isLastRead),
        ],
      };
    }
    case BookmarkAction.ADD_READ_BOOKMARK: {
      const bookmark = action.bookmark as IReadBookmark;
      return {
        ...state,
        items: [new ReadBookmark(bookmark), ...state.items],
      };
    }
    case BookmarkAction.REMOVE_READ_BOOKMARK: {
      const bookmark = action.bookmark as ReadBookmark;
      return {
        ...state,
        items: removeElementsFromArray([...state.items], bookmark),
      };
    }
  }
}

export const ReadBookmarksContext =
  React.createContext<IReadBookmarksContext | null>(null);

const initialState = {
  items: [],
  isBookmark(surah: ReadSurah, timing?: number) {
    if (Number.isInteger(timing)) {
      return this.items.find((readBookmark) => {
        return (
          readBookmark?.readSurah?.slug === surah.slug &&
          readBookmark.ayaIndex === timing &&
          !readBookmark.isLastRead
        );
      });
    }
    return this.items.find((readBookmark) => {
      return (
        readBookmark?.readSurah?.slug === surah.slug && !readBookmark.isLastRead
      );
    });
  },
} as IReadBookmarkState;

const ReadBookmarksProvider: React.FC<IReadBookmarksProvider> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducerFunc, initialState);

  const isBookmarked = (surah: ReadSurah, timing?: number) => {
    if (Number.isInteger(timing)) {
      return state.items.find((readBookmark) => {
        return (
          readBookmark?.readSurah?.slug === surah.slug &&
          readBookmark.ayaIndex === timing &&
          !readBookmark.isLastRead
        );
      });
    }
    return state.items.find((readBookmark) => {
      return (
        readBookmark?.readSurah?.slug === surah.slug && !readBookmark.isLastRead
      );
    });
  };

  const loadReadBookmarks = async (user: User) => {
    try {
      const { db, collection, getDocs, orderBy, query } =
        await getFirestoreClient();
      const res: IReadBookmark[] = [];
      const collectionRef = readBookmarkCollectionRef(
        db,
        collection,
        user.uid
      );
      const q = query(collectionRef, orderBy('createdDate', 'desc'));
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        const docData = doc.data() as IReadBookmark;
        res.push(docData);
      });
      const api = await getApiClient();
      const readSurahs: IReadBookmark[] = [];
      for (const bookmark of res) {
        const { data } = await api.getReadSurahBySurahNumber(
          bookmark.suraIndex
        );
        const { surah } = data;
        readSurahs.push({
          ...bookmark,
          readSurah: surah,
        });
      }

      dispatch({ type: BookmarkAction.SET_BOOKMARKS, items: readSurahs });
    } catch (error) {
      console.error(error);
    }
  };

  const loadLastRead = async (user: User) => {
    try {
      const { db, doc, getDoc } = await getFirestoreClient();
      const docRef = lastReadDocRef(db, doc, user.uid);
      const lastReadDoc = await getDoc(docRef);
      const docData = lastReadDoc.data() as ILastRead;
      if (docData) {
        const api = await getApiClient();
        const { data } = await api.getReadSurahBySurahNumber(docData.sura);
        const readSurah = data.surah as IReadSurah;
        const bookmark = {
          ayaIndex: docData.aya,
          page: docData.page,
          suraAName: docData.aName,
          suraIndex: docData.sura,
          suraTName: docData.tName,
          isLastRead: true,
          readSurah,
        };
        dispatch({ type: BookmarkAction.SET_LAST_READ, bookmark });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const saveLastRead = async ({
    user,
    readSurah,
    ayaNumber,
  }: IBaseReadBookmarkUpdateEffect) => {
    try {
      const { db, doc, setDoc } = await getFirestoreClient();
      const lastRead = {
        page: readSurah.page,
        aName: readSurah.titleAr,
        sura: readSurah.index,
        tName: readSurah.transliteration,
        savedAsPage: true,
        aya: ayaNumber,
      };
      const docRef = lastReadDocRef(db, doc, user.uid);
      await setDoc(docRef, lastRead);
      const api = await getApiClient();
      const { data } = await api.getReadSurahBySurahNumber(readSurah.index);
      if (data && ayaNumber) {
        const res = data.surah as IReadSurah;
        const bookmark = {
          ayaIndex: ayaNumber,
          page: readSurah.page,
          suraAName: readSurah.titleAr,
          suraIndex: readSurah.index,
          suraTName: readSurah.transliteration,
          isLastRead: true,
          readSurah: res,
        };
        dispatch({ type: BookmarkAction.SET_LAST_READ, bookmark });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const addToBookmark = async ({
    user,
    readSurah,
    ayaNumber,
  }: IBaseReadBookmarkUpdateEffect) => {
    try {
      const { db, addDoc, collection } = await getFirestoreClient();
      const readBookmark: any = {
        ayaIndex: 1,
        createdDate: new Date(),
        page: readSurah.page,
        suraAName: readSurah.titleAr,
        suraIndex: readSurah.index,
        suraTName: readSurah.transliteration,
      } as IReadBookmark;

      if (ayaNumber) {
        readBookmark.ayaIndex = ayaNumber;
      }

      const collectionRef = readBookmarkCollectionRef(
        db,
        collection,
        user.uid
      );

      await addDoc(collectionRef, readBookmark);

      const api = await getApiClient();
      const { data } = await api.getReadSurahBySurahNumber(readSurah.index);
      readBookmark.readSurah = data.surah;
      dispatch({
        type: BookmarkAction.ADD_READ_BOOKMARK,
        bookmark: readBookmark,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const removeFromBookmark = async ({
    user,
    bookmark,
  }: IDeleteReadBookmarkEffect) => {
    try {
      const { db, collection, deleteDoc, doc, getDocs, query, where } =
        await getFirestoreClient();
      const collectionRef = readBookmarkCollectionRef(
        db,
        collection,
        user.uid
      );
      const q = query(
        collectionRef,
        where('suraIndex', '==', bookmark.suraIndex),
        where('suraAName', '==', bookmark.suraAName),
        where('suraTName', '==', bookmark.suraTName),
        where('page', '==', bookmark.page),
        where('ayaIndex', '==', bookmark.ayaIndex)
      );
      const docsRef = await getDocs(q);
      if (docsRef.docs[0]) {
        const docRef = readBookmarkDocRef(
          db,
          doc,
          user.uid,
          docsRef.docs[0].id
        );
        await deleteDoc(docRef);
        dispatch({ type: BookmarkAction.REMOVE_READ_BOOKMARK, bookmark });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const changeReadBookmark = async ({
    user,
    readSurah,
    ayaNumber,
  }: IBaseReadBookmarkUpdateEffect) => {
    const bookmark = isBookmarked(readSurah, ayaNumber);
    if (bookmark) {
      await removeFromBookmark({ user, bookmark });
    } else {
      await addToBookmark({
        user,
        readSurah,
        ayaNumber,
      });
    }
  };

  const value = {
    readBookmarks: state,
    loadReadBookmarks,
    loadLastRead,
    saveLastRead,
    addToBookmark,
    removeFromBookmark,
    changeReadBookmark,
    isBookmarked,
  };

  return (
    <ReadBookmarksContext.Provider value={value}>
      {children}
    </ReadBookmarksContext.Provider>
  );
};

export const useReadBookmarks = () =>
  useContext(ReadBookmarksContext) as IReadBookmarksContext;

export default ReadBookmarksProvider;
